/**
 * In-app reminders, as a dismissible tray in the shell.
 *
 * A POLL, not a subscription. One query a minute per open tab is cheaper than
 * holding a socket open for each of them, and the tray is not a real-time
 * surface — a reminder that appears within a minute of firing is a reminder
 * that worked. A subscription is roadmap (todo.md), and would be the right
 * answer if this ever needed to be instant.
 *
 * It shows reminders the server has already FIRED, not ones that are merely
 * due. There is one firing path and this reads its output; computing "due" here
 * would be a second, competing one that could disagree with the events on the bus.
 *
 * Dismissal is local and per-tab, deliberately. A reminder is a nudge, not a
 * task: two tabs each dismissing their own copy is fine, and persisting the
 * dismissal would need a mutation, a table and a decision about what "seen"
 * means across devices — all for something the user will act on in the next
 * thirty seconds.
 */
import { useQuery } from '@apollo/client';
import { AlertTriangle, Bell, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { REMINDER_POLL_MS } from '../constants/density.js';
import { formatTime } from '../lib/format.js';
import { DUE_REMINDERS_QUERY, URGENT_ISSUES_QUERY } from '../lib/queries.js';
import { S } from '../lib/strings.js';

export function ReminderTray() {
  const [dismissed, setDismissed] = useState(() => new Set());

  // A fixed `since` per mount rather than a moving window: a value recomputed
  // on every render would change the query variables every render and poll in
  // a loop.
  const since = useMemo(
    () => new Date(Date.now() - 12 * 3600 * 1000).toISOString(),
    [],
  );

  const { data } = useQuery(DUE_REMINDERS_QUERY, {
    variables: { since },
    pollInterval: REMINDER_POLL_MS,
    fetchPolicy: 'cache-and-network',
    // A failing poll must not take the shell down with it.
    errorPolicy: 'ignore',
  });

  // Step 5: urgent issues nobody has taken, on the same poll. See
  // URGENT_ISSUES_QUERY for why this is a query rather than the event the
  // feedback service emits.
  const { data: urgentData } = useQuery(URGENT_ISSUES_QUERY, {
    pollInterval: REMINDER_POLL_MS,
    errorPolicy: 'ignore',
  });

  const items = (data?.dueReminders ?? []).filter(
    (entry) => !dismissed.has(entry.reminder.id),
  );
  const urgent = (urgentData?.issues?.items ?? []).filter(
    (issue) => !dismissed.has(issue.id),
  );

  if (items.length === 0 && urgent.length === 0) return null;

  return (
    <aside
      className="flex flex-col gap-1 border-t border-border bg-surface p-3"
      aria-label={S.reminders.title}
    >
      <div className="flex items-center gap-2 text-xs font-medium text-text-muted">
        <Bell size={13} aria-hidden="true" />
        {S.reminders.title}
        <button
          type="button"
          className="ms-auto hover:text-text"
          onClick={() =>
            setDismissed(
              new Set([...items.map((i) => i.reminder.id), ...urgent.map((i) => i.id)]),
            )
          }
        >
          {S.reminders.dismissAll}
        </button>
      </div>

      {items.map(({ reminder, activity }) => (
        <div
          key={reminder.id}
          className="flex items-start gap-2 rounded border border-border px-2 py-1"
        >
          <Link
            to="/calendar"
            className="flex flex-1 flex-col text-xs leading-tight hover:text-primary"
          >
            <span className="truncate font-medium">{activity.title}</span>
            <span className="text-text-muted">
              <span dir="ltr">{formatTime(activity.startsAt)}</span>
              {activity.contact?.displayName ? ` · ${activity.contact.displayName}` : ''}
            </span>
          </Link>
          <button
            type="button"
            aria-label={S.reminders.dismiss}
            onClick={() =>
              setDismissed((current) => new Set(current).add(reminder.id))
            }
            className="text-text-muted hover:text-text"
          >
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      ))}

      {urgent.map((issue) => (
        <div
          key={issue.id}
          className="flex items-start gap-2 rounded border border-danger px-2 py-1"
        >
          <Link
            to={`/issues/${issue.id}`}
            className="flex flex-1 flex-col text-xs leading-tight hover:text-primary"
          >
            <span className="flex items-center gap-1 truncate font-medium text-danger">
              <AlertTriangle size={11} aria-hidden="true" />
              {issue.title}
            </span>
            <span className="text-text-muted">
              {S.issues.urgentUnassigned}
              {issue.contact?.displayName ? ` · ${issue.contact.displayName}` : ''}
            </span>
          </Link>
          <button
            type="button"
            aria-label={S.reminders.dismiss}
            onClick={() => setDismissed((current) => new Set(current).add(issue.id))}
            className="text-text-muted hover:text-text"
          >
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      ))}
    </aside>
  );
}
